'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatedCounter } from '@/components/ui/AnimatedCounter';
import { PRODUCTS_TOTAL, PRODUCTS_LIVE, INDUSTRIES } from '@/lib/site-stats';

type Stone = {
  value: number;
  suffix: string;
  label: string;
  spark: number[];
  highlight?: boolean;
};

// 12-week trailing trend sparkline data — illustrative shape, not real-time.
// Each stone gets a hand-tuned silhouette so the four arcs don't feel
// algorithmic; they should read like four different small ponds.
const STONES: Stone[] = [
  { value: PRODUCTS_TOTAL, suffix: '',  label: 'Products Shipped', spark: [1, 2, 2, 3, 4, 4, 5, 6, 7, 7, 8, 8] },
  { value: INDUSTRIES,     suffix: '',  label: 'Industries',       spark: [1, 1, 2, 2, 3, 3, 3, 4, 4, 5, 5, 5] },
  { value: PRODUCTS_LIVE,  suffix: '',  label: 'Live Products',    spark: [1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 6], highlight: true },
];

const SIZE = 168;
const SPARK_RADIUS = 44;     // pulled inside the stone as a contained interior chart
const BREATH_BASE_MS = 5400;

export function HeroStones() {
  const [scrollY, setScrollY] = useState(0);
  const rafRef = useRef<number | null>(null);
  const ticking = useRef(false);

  useEffect(() => {
    function onScroll() {
      if (ticking.current) return;
      ticking.current = true;
      rafRef.current = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        ticking.current = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 w-full max-w-2xl"
      style={{
        animation: 'fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) 0.9s both',
      }}
    >
      {STONES.map((stone, idx) => {
        // ±4px parallax against background, alternating phase per stone
        const parallax = Math.sin(scrollY / 320 + idx * 0.9) * 4;
        // Breath animation offset per stone so they breathe out of phase
        const breathDelay = (idx * 0.6).toFixed(2);
        const valueColor = stone.highlight ? 'var(--accent)' : 'var(--text-primary)';
        return (
          <div
            key={stone.label}
            className="flex flex-col items-center"
            style={{
              transform: `translateY(${parallax.toFixed(2)}px)`,
              transition: 'transform 600ms var(--ease-calm)',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: SIZE,
                height: SIZE,
                animation: `breath ${BREATH_BASE_MS}ms ease-in-out ${breathDelay}s infinite`,
              }}
            >
              {/* Inner glow circle */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle at 50% 45%, color-mix(in srgb, var(--accent) 14%, transparent) 0%, color-mix(in srgb, var(--accent) 4%, transparent) 50%, transparent 78%)',
                  filter: 'blur(0.5px)',
                }}
              />
              {/* Thin animated ring */}
              <svg
                aria-hidden
                width={SIZE}
                height={SIZE}
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                style={{ position: 'absolute', inset: 0 }}
              >
                {/* Outer ring — full perimeter, sealed pebble */}
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={SIZE / 2 - 4}
                  fill="none"
                  stroke="color-mix(in srgb, var(--accent) 32%, transparent)"
                  strokeWidth={1}
                />
                {/* Inner ring — full perimeter, decorative depth */}
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={SIZE / 2 - 12}
                  fill="none"
                  stroke="var(--border)"
                  strokeWidth={1}
                />
                {/* Sparkline — small interior chart above the number */}
                <SparkArc data={stone.spark} cx={SIZE / 2} cy={SIZE / 2 - 12} r={SPARK_RADIUS} />
              </svg>

              {/* Number — centered */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <AnimatedCounter
                  value={stone.value}
                  suffix={stone.suffix}
                  className="font-mono"
                  style={{
                    color: valueColor,
                    fontSize: 44,
                    fontWeight: 800,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                />
              </div>
            </div>
            <span
              className="mt-3 font-mono text-text-muted text-center"
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              {stone.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SparkArc({
  data,
  cx,
  cy,
  r,
}: {
  data: number[];
  cx: number;
  cy: number;
  r: number;
}) {
  // Interior chart: trend line sweeping a 120° arc near the top of the stone,
  // contained well inside the sealed outer ring. Data normalized to a ±6px
  // radial wave so the line reads as a tiny trend, not a busy waveform.
  if (data.length === 0) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = Math.max(1, max - min);
  const startDeg = -150;
  const endDeg = -30;
  const points = data.map((v, i) => {
    const t = data.length === 1 ? 0 : i / (data.length - 1);
    const deg = startDeg + (endDeg - startDeg) * t;
    const radNorm = (v - min) / range;
    const radiusOffset = -6 + radNorm * 12;
    const rad = (deg * Math.PI) / 180;
    const radius = r + radiusOffset;
    return [cx + Math.cos(rad) * radius, cy + Math.sin(rad) * radius] as const;
  });
  const path = points
    .map((p, i) => (i === 0 ? `M ${p[0].toFixed(1)} ${p[1].toFixed(1)}` : `L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`))
    .join(' ');
  return (
    <path
      d={path}
      fill="none"
      stroke="color-mix(in srgb, var(--accent) 55%, transparent)"
      strokeWidth={1.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}
